import {
	Action,
	configureStore,
	ThunkAction,
	PreloadedState,
	StateFromReducersMapObject,
} from '@reduxjs/toolkit';
import { GlobalStateSlice } from './reducers/index';
import { useMemo } from 'react';
import { LoginSlice } from './reducers/login';
import { SignupSlice } from './reducers/signup';
import { RecaptchaSlice } from './reducers/recaptcha';
import { RecoverySlice } from './reducers/recovery';
import { RecoveryConfirmSlice } from './reducers/recoveryConfirm';

const apiEndpoint: string = process.env.NEXT_PUBLIC_HOME_BASE_API ?? ""
export interface IThunkApiExtra {
	apiEndpoint: string,
	services: () => Promise<typeof import("../../services.setup.client")>
}

const reducer = {
	GlobalState: GlobalStateSlice.reducer,
	LoginState: LoginSlice.reducer,
	SignupState: SignupSlice.reducer,
	RecaptchaState: RecaptchaSlice.reducer,
	RecoveryState: RecoverySlice.reducer,
	RecoveryConfirmState: RecoveryConfirmSlice.reducer,

}

export type RootState = StateFromReducersMapObject<typeof reducer>

function initStore(preloadedState?: PreloadedState<RootState>) {
	return configureStore({
		reducer,
		preloadedState,
		devTools: process.env.NODE_ENV !== 'production',
		middleware: getDefaultMiddleware => getDefaultMiddleware({
			thunk: {
				extraArgument: {
					apiEndpoint,
					services: async () => (await import('../../services.setup.client'))
				}
			}
		})
	});
}

export type AppStore = ReturnType<typeof initStore>
export type AppDispatch = AppStore["dispatch"];

let store: ReturnType<typeof initStore> | undefined;
// This could be helpful
// https://github.com/vercel/next.js/blob/next-13/examples/with-redux-thunk/store.js
export function setupStore(preloadedState?: PreloadedState<RootState>) {
	let _store = store ?? initStore(preloadedState)

	// After navigating to a page with an initial Redux state, merge that state
	// with the current state in the store, and create a new store
	if (preloadedState && store) {
		_store = initStore({
			...store.getState(),
			...preloadedState,
		})
		// Reset the current store
		store = undefined
	}

	// For SSG and SSR always create a new store
	if (typeof window === 'undefined') return _store
	// Create the store once in the client
	if (!store) store = _store

	return _store

	// return initStore(preloadedState)
}

export function useStore(initialState? : PreloadedState<RootState>) {
	const store = useMemo(() => setupStore(initialState), [initialState])
	return store
}

export const selectGlobal = (state: RootState) => state.GlobalState;

export type AppThunk<ReturnType = void> = ThunkAction<
	ReturnType,
	RootState,
	unknown | { apiEndpoint: string | undefined },
	Action<string>
>;